import React, { useMemo, useState } from "react";
import { parseLocalDateFromYmd, toApiDateParam } from "../utils/dateRangeApiFormat";

/**
 * Reusable date range filter with quick presets and from/to pickers.
 *
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - onApply: ({ dateFrom: string|null, dateTo: string|null, presetId: string|null }) => void
 * - initialRange?: { dateFrom: string|null, dateTo: string|null, presetId?: string|null }
 */
const PRESETS = [
  { id: "this_week", label: "This week" },
  { id: "last_week", label: "Last week" },
  { id: "this_month", label: "This month" },
  { id: "last_month", label: "Last month" },
  { id: "this_year", label: "This year" },
  { id: "last_year", label: "Last year" },
  { id: "custom", label: "Custom" },
];

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfDay = (date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

const toInputDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getPresetRange = (presetId) => {
  const now = new Date();
  const today = startOfDay(now);

  switch (presetId) {
    case "this_week": {
      const day = today.getDay(); // 0-6 (Sun-Sat)
      const diffToMonday = (day + 6) % 7; // convert Sun=6, Mon=0, ...
      const from = new Date(today);
      from.setDate(today.getDate() - diffToMonday);
      const to = new Date(from);
      to.setDate(from.getDate() + 6);
      return { from, to };
    }
    case "last_week": {
      const day = today.getDay();
      const diffToMonday = (day + 6) % 7;
      const thisMonday = new Date(today);
      thisMonday.setDate(today.getDate() - diffToMonday);
      const from = new Date(thisMonday);
      from.setDate(thisMonday.getDate() - 7);
      const to = new Date(from);
      to.setDate(from.getDate() + 6);
      return { from, to };
    }
    case "this_month": {
      const from = new Date(today.getFullYear(), today.getMonth(), 1);
      const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { from, to };
    }
    case "last_month": {
      const from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const to = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from, to };
    }
    case "this_year": {
      const from = new Date(today.getFullYear(), 0, 1);
      const to = new Date(today.getFullYear(), 11, 31);
      return { from, to };
    }
    case "last_year": {
      const from = new Date(today.getFullYear() - 1, 0, 1);
      const to = new Date(today.getFullYear() - 1, 11, 31);
      return { from, to };
    }
    default:
      return { from: null, to: null };
  }
};

const DateRangeFilter = ({ isOpen, onClose, onApply, initialRange }) => {
  const [activePreset, setActivePreset] = useState(
    initialRange?.presetId || "this_month"
  );

  const [fromInput, setFromInput] = useState(() =>
    toInputDate(initialRange?.dateFrom || null)
  );
  const [toInput, setToInput] = useState(() =>
    toInputDate(initialRange?.dateTo || null)
  );

  // When a preset is selected, pre-fill the inputs (visual feedback)
  useMemo(() => {
    if (!activePreset || activePreset === "custom") return;
    const { from, to } = getPresetRange(activePreset);
    if (from && to) {
      const fromStr = toInputDate(from.toISOString());
      const toStr = toInputDate(to.toISOString());
      setFromInput(fromStr);
      setToInput(toStr);
    }
  }, [activePreset]);

  if (!isOpen) return null;

  const handleApply = () => {
    let fromDate = parseLocalDateFromYmd(fromInput);
    let toDate = parseLocalDateFromYmd(toInput);

    if (fromDate && toDate && fromDate > toDate) {
      const tmp = fromDate;
      fromDate = toDate;
      toDate = tmp;
    }

    const payload = {
      dateFrom: fromDate ? toApiDateParam(fromDate, false) : null,
      dateTo: toDate ? toApiDateParam(toDate, true) : null,
      presetId: activePreset || null,
    };

    onApply?.(payload);
    onClose?.();
  };

  const handleClear = () => {
    setFromInput("");
    setToInput("");
    setActivePreset("custom");
    onApply?.({ dateFrom: null, dateTo: null, presetId: null });
    onClose?.();
  };

  return (
    <div className="date-filter-overlay" onClick={onClose}>
      <div
        className="date-filter-popover"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="date-filter-sidebar">
          <div className="date-filter-title">Filters</div>
          <div className="date-filter-subtitle">Date</div>
          <div className="date-filter-preset-list">
            {PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                className={`date-filter-preset-btn ${
                  activePreset === preset.id
                    ? "date-filter-preset-btn--active"
                    : ""
                }`}
                onClick={() => setActivePreset(preset.id)}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>

        <div className="date-filter-content">
          <div className="date-filter-inputs">
            <div className="date-filter-input-group">
              <label htmlFor="date-from">From</label>
              <input
                id="date-from"
                type="date"
                value={fromInput}
                onChange={(e) => {
                  setFromInput(e.target.value);
                  setActivePreset("custom");
                }}
              />
            </div>
            <div className="date-filter-input-group">
              <label htmlFor="date-to">To</label>
              <input
                id="date-to"
                type="date"
                value={toInput}
                onChange={(e) => {
                  setToInput(e.target.value);
                  setActivePreset("custom");
                }}
              />
            </div>
          </div>

          <div className="date-filter-footer">
            <button
              type="button"
              className="date-filter-btn date-filter-btn--secondary"
              onClick={handleClear}
            >
              Clear
            </button>
            <button
              type="button"
              className="date-filter-btn date-filter-btn--primary"
              onClick={handleApply}
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DateRangeFilter;

